import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import LocalPrintshopRoundedIcon from "@mui/icons-material/LocalPrintshopRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import PDFMerger from "pdf-merger-js/browser";
import print from "print-js";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CoreButtonHoverIcon from "../../../../../app/core/components/CoreButtonIcon/CoreButtonHoverIcon";
import CoreDialog from "../../../../../app/core/components/CoreDialog/CoreDialog";
import { ROLE, SUB_ROLE } from "../../../../../utils/constants/profiles";
import { convertJpgToPdf } from "../../../../../utils/functions/convertInPDF";
import { actions } from "../../gestisciDocumentiSlice";
import CoreSpeedDial from "../../../../../app/core/components/CoreSpeedDial/CoreSpeedDial";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";
import HighlightOffRoundedIcon from "@mui/icons-material/HighlightOffRounded";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { IconButton, Tooltip } from "@mui/material";
import { adminDocumentsApi } from "../../../../api/admin/gestisciDocumentiApi";

const GestioneDocumentiButtonCockpit = ({
  deleteDocuments,
  refetch,
  data,
  isDeleteLoading,
  validateAllDocument,
  rejectAllDocument,
  reanalyzeAllDocument,
}) => {
  const { t } = useTranslation();
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const role = useSelector((state) => state.app.role);
  const subrole = useSelector((state) => state.app.subRole);
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const docManagementState = useSelector((state) => state.docManagementSlice);
  const isSelectedDocsEmpty = docManagementState.selectedDocsIds.length === 0;

  const getLocalFile = (data, Ids) => {
    const files = [];
    data.forEach((doc) => {
      if (Ids.includes(doc.id)) {
        const { extension } = doc.files[0];
        const { name } = doc;
        const url = `${process.env.REACT_APP_API_URL}${doc.files[0].url}`;
        files.push({ url, extension, name });
      }
    });
    return files;
  };

  // UPDATE DOCUMENTS
  const handleRefecthDocuments = () => {
    refetch();
  };

  const confirmDeleteDocuments = () => {
    deleteDocuments(docManagementState.selectedDocsIds).then((res) => {
      if (res.data.Result === 1) {
        dispatch(actions.setSelectedDocsIds([]));
        setOpenDeleteDialog(false);
        toast.success(t("DocsDeleted"));
      } else if (res.data.Exception) {
        toast.error(res.data.Exception);
      }
    });
  };

  const handleDeleteDocuments = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDownloadZip = async () => {
    const zip = new JSZip();
    const files = getLocalFile(data, docManagementState.selectedDocsIds);
    const id = toast.loading(t("HandleDownload"), {
      pauseOnFocusLoss: false,
      type: "info",
    });

    for (const file of files) {
      const response = await fetch(file.url);
      const blob = await response.blob();
      zip.file(file.name + "." + file.extension, blob);
    }

    zip.generateAsync({ type: "blob" }).then((content) => {
      saveAs(content, "documents.zip");
      toast.update(id, {
        render: t("DocReady"),
        type: "success",
        isLoading: false,
        autoClose: 1500,
      });
    });
  };

  const handlePrintDocuments = async () => {
    dispatch(actions.setSelectedDocsIds([]));
    const files = getLocalFile(data, docManagementState.selectedDocsIds);
    const merger = new PDFMerger();
    const id = toast.loading(t("HandleDownload"), {
      pauseOnFocusLoss: false,
      type: "info",
    });
    for (const file of files) {
      if (file.extension.toLowerCase() !== "pdf") {
        let convertedBlob = null;
        convertedBlob = await convertJpgToPdf(file.url);
        if (convertedBlob) {
          await merger.add(convertedBlob);
        }
      } else {
        let blob = await fetch(file.url).then((r) => r.blob());
        await merger.add(blob);
      }
    }
    await merger.setMetadata({
      producer: "pdf-merger-js based script",
    });
    const mergedPdf = await merger.saveAsBlob();
    const url = URL.createObjectURL(mergedPdf);
    toast.update(id, {
      render: t("DocReady"),
      type: "success",
      isLoading: false,
      autoClose: 1500,
    });
    print({
      printable: url,
    });
  };

  const handleDocumentsValidateAll = () => {
    validateAllDocument(docManagementState.selectedDocsIds)
      .then((response) => {
        if (response.data.Result !== 1) {
          toast.error(response.data.Exception || t("DocsNotValidated"));
          return;
        }
        toast.success(t("DocsValidated"));
      })
      .catch((error) => {
        toast.error(t("DocsNotValidated") + " " + (error?.message || ""));
      });
    dispatch(actions.setSelectedDocsIds([]));
  };

  const handleDocumentsReanalyzePendings = () => {
    reanalyzeAllDocument(docManagementState.selectedDocsIds)
      .then((response) => {
        if (response.data.Result === 1) {
          toast.success(t("DocsAnalyzed"));
          dispatch(adminDocumentsApi.util.invalidateTags(["documents"]));
        } else {
          toast.error(response.data.Exception || t("DocsNotAnalyzed"));
        }
      })
      .catch((error) => {
        toast.error(t("DocsNotAnalyzed") + " " + (error?.message || ""));
      });
    dispatch(actions.setSelectedDocsIds([]));
  };

  const handleRejectDocuments = () => {
    let rejectBody = {
      ids: docManagementState.selectedDocsIds,
    };
    rejectAllDocument(rejectBody)
      .then((response) => {
        if (response.data.Result === 1) {
          toast.success(t("DocsDiscarded"));
        } else {
          toast.error(response.data.Exception || t("DocsNotDiscarded"));
        }
      })
      .catch((error) => {
        toast.error(t("DocsNotDiscarded") + " " + (error?.message || ""));
      });
    dispatch(actions.setSelectedDocsIds([]));
  };

  const multiActions = [
    {
      icon: (
        <InsightsRoundedIcon
          className={isSelectedDocsEmpty ? `fill-gray-200` : `fill-yellow-500`}
        />
      ),
      name: t("Reanalyze"),
      onClick: handleDocumentsReanalyzePendings,
    },
    {
      icon: (
        <CheckCircleOutlineRoundedIcon
          className={isSelectedDocsEmpty ? `fill-gray-200` : `text-green-700`}
        />
      ),
      name: t("Validate"),
      onClick: handleDocumentsValidateAll,
    },
    {
      icon: (
        <HighlightOffRoundedIcon
          className={isSelectedDocsEmpty ? `fill-gray-200` : `fill-orange-500`}
        />
      ),
      name: t("Discard"),
      onClick: handleRejectDocuments,
    },
    {
      icon: (
        <DeleteRoundedIcon
          className={isSelectedDocsEmpty ? `fill-gray-200` : `fill-rose-600`}
        />
      ),
      name: t("Delete"),
      onClick: handleDeleteDocuments,
    },

    {
      icon: (
        <DownloadRoundedIcon
          className={isSelectedDocsEmpty ? `text-gray-200` : `fill-blue-700`}
        />
      ),
      name: t("Download"),
      onClick: handleDownloadZip,
    },
    {
      icon: (
        <LocalPrintshopRoundedIcon
          className={isSelectedDocsEmpty ? `text-gray-200` : `fill-black`}
        />
      ),
      name: t("Print"),
      onClick: handlePrintDocuments,
    },
  ];

  return (
    <div className="flex w-full items-center">
      <CoreButtonHoverIcon
        onClick={handleRefecthDocuments}
        className="text-blue-500 m-1 flex flex-row space-x-2 border"
        icon={<RefreshRoundedIcon className={`fill-blue-500`} />}
        testo={t("Refresh")}
      />

      <Tooltip title={t("UploadDoc")}>
        <IconButton
          size="large"
          onClick={() =>
            role === ROLE.BUSINESS
              ? subrole === SUB_ROLE.ADMIN
                ? navigate("/auth/business/" + t("uploadDocuments"))
                : navigate("/auth/operator/" + t("uploadDocuments"))
              : navigate("/auth/admin/" + t("uploadDocuments"))
          }
          className="text-green-500 m-1 flex flex-row space-x-2 border"
        >
          <UploadFileRoundedIcon fontSize="30" className={`fill-green-500`} />
        </IconButton>
      </Tooltip>
      <CoreSpeedDial actions={multiActions} selectedDoc={isSelectedDocsEmpty} />

      <CoreDialog
        open={openDeleteDialog}
        title={t("DocsDeleting")}
        content={t("DocsDeletingConfirm")}
        positiveButton={t("Yes")}
        negativeButton={t("No")}
        handlePositive={confirmDeleteDocuments}
        closeDialog={handleCloseDeleteDialog}
        disabledButton={isDeleteLoading}
      />
    </div>
  );
};

export default GestioneDocumentiButtonCockpit;
